import { Box, Button, Typography } from '@mui/material';
import { describeQueryError } from '../../api/describeQueryError.api';
import type { SyncRun } from '../../api/admin.api';
import { useListSyncRunsQuery, useTriggerSyncMutation } from '../../api/admin.api';
import { formatDateTime } from '../../content/adminDates';
import { cardSurfaceSx } from '../../theme';
import { SYNC_TASKS } from './syncTasks';

/** Seconds between refreshes while a sync is running, so its outcome arrives without a reload. */
const POLL_WHILE_RUNNING_MS = 5000;

const describeRun = (run: SyncRun | undefined): string => {
  if (run === undefined) {
    return 'Not run in the last 90 days.';
  }

  const by = run.trigger === 'schedule' ? 'on schedule' : `by ${run.trigger}`;
  const when = formatDateTime(run.startedAt) ?? run.startedAt;

  if (run.status === 'running') {
    return `Running since ${when}, ${by}.`;
  }

  return `${run.status === 'succeeded' ? 'Succeeded' : 'Failed'} ${when}, ${by}.`;
};

/**
 * One button per sync, with its last run beside it. **Pressing it starts the sync and
 * answers at once**: a run lasts minutes, so the row polls until it is no longer
 * running. A run limited to one repository is that repository's button, below, and is
 * shown here too, as the newest run of the git sync.
 */
export const SyncsSection = () => {
  const { data: runs, isLoading } = useListSyncRunsQuery();
  const [trigger, { isLoading: isStarting, error: startError, originalArgs }] = useTriggerSyncMutation();

  const anyRunning = runs?.some((run) => run.status === 'running') === true;
  // Re-subscribes with a polling interval only while something is running: a polling
  // query that is always on is a request every few seconds for a page nobody is reading.
  useListSyncRunsQuery(undefined, { pollingInterval: anyRunning ? POLL_WHILE_RUNNING_MS : 0 });

  return (
    <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {SYNC_TASKS.map(({ task, label, description, schedule }) => {
        const run = runs?.find((entry) => entry.task === task);
        const running = run?.status === 'running';

        return (
          <Box component="li" key={task} sx={{ ...cardSurfaceSx, padding: 5, display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ maxWidth: 640 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {description} {schedule}.
              </Typography>
              <Typography variant="body2" role="status" sx={{ marginTop: 1 }}>
                {isLoading ? 'Loading the last run.' : describeRun(run)}
              </Typography>
              {run?.problems !== undefined && run.problems.length > 0 && (
                <Typography variant="body2" sx={{ color: 'error.main', marginTop: 1 }}>
                  {run.problems[0]}
                </Typography>
              )}
              {startError !== undefined && originalArgs?.task === task && (
                <Typography variant="body2" role="alert" sx={{ color: 'error.main', marginTop: 1 }}>
                  {describeQueryError(startError)}
                </Typography>
              )}
            </Box>

            <Button
              variant="outlined"
              size="small"
              disabled={running || isStarting}
              onClick={() => void trigger({ task })}
              aria-label={`Run ${label} now`}
            >
              {running ? 'Running' : 'Run now'}
            </Button>
          </Box>
        );
      })}
    </Box>
  );
};
